import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/utils/utils';

interface FullPageShellProps {
  step?: 1 | 2;
  totalSteps?: number;
  showProgress?: boolean;
  children: ReactNode;
  contentClassName?: string;
}

export function FullPageShell({
  step,
  totalSteps = 2,
  showProgress = true,
  children,
  contentClassName,
}: FullPageShellProps) {
  const { t } = useTranslation('common');
  return (
    <div
      className={cn(
        'min-h-screen w-full flex flex-col',
        'bg-page-bg',
        'bg-[radial-gradient(60%_50%_at_50%_0%,rgba(34,209,197,0.12),transparent_70%),radial-gradient(40%_40%_at_85%_85%,rgba(22,120,194,0.10),transparent_75%)]'
      )}
    >
      <header className="grid grid-cols-3 items-center px-6 py-5 sm:px-10">
        <img
          src="/intera_logo_with_text.svg"
          alt="Intera"
          className="h-7 w-auto justify-self-start"
        />
        {showProgress && step !== undefined ? (
          <div
            className="flex items-center justify-center gap-2"
            aria-label={t('fullPageShell.progressAriaLabel', {
              step,
              total: totalSteps,
            })}
          >
            {Array.from({ length: totalSteps }).map((_, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === step;
              const isComplete = stepNumber < step;
              return (
                <span
                  key={stepNumber}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    isActive && 'w-8 bg-primary',
                    isComplete && 'w-4 bg-primary/60',
                    !isActive && !isComplete && 'w-4 bg-foreground/10'
                  )}
                />
              );
            })}
          </div>
        ) : (
          <span />
        )}
        <span />
      </header>
      <main className={cn('flex-1 flex flex-col', contentClassName)}>
        {children}
      </main>
    </div>
  );
}
